import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, Search, Copy, Loader2, Check, X, Timer } from "lucide-react";
import { byLastName } from "@/lib/format";

export function ReissueTapDialog({ open, onOpenChange, userId, employeeName }) {
  const { t } = useLang();
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState(null);
  const [tapPolicy, setTapPolicy] = useState(null);
  const [tapLifetime, setTapLifetime] = useState(60);
  const [tapStart, setTapStart] = useState("");

  const deliveryConfigured = ["toManager", "toCreator", "toFixed", "toManual"].some((k) => settings?.passwordDelivery?.[k]);

  useEffect(() => {
    if (!open) return;
    setContactId(""); setQ(""); setResult(null); setCopied(false); setTapStart("");
    api.get("/users").then((r) => setContacts(r.data.filter((u) => !u.isShared))).catch(() => {});
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setTapLifetime(r.data?.tap?.lifetimeInMinutes || 60);
    }).catch(() => {});
    api.get("/settings/tap-policy").then((r) => setTapPolicy(r.data)).catch(() => {});
  }, [open]);

  const list = useMemo(() => contacts.filter((u) => `${u.displayName} ${u.userPrincipalName}`.toLowerCase().includes(q.toLowerCase())).sort(byLastName).slice(0, 30), [contacts, q]);
  const picked = contacts.find((u) => u.id === contactId);

  const submit = async () => {
    if (!contactId && !deliveryConfigured) { toast.error(t("pw.tapNeedsDelivery")); return; }
    setBusy(true);
    try {
      const body = {
        notifyContactId: contactId || null,
        tapLifetimeInMinutes: Number(tapLifetime) || undefined,
        tapStartDateTime: tapStart ? new Date(tapStart).toISOString() : undefined,
      };
      const { data } = await api.post(`/users/${userId}/reissue-tap`, body);
      setResult(data);
      toast.success(t("pw.reissueDone"));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const copy = async () => { await navigator.clipboard.writeText(result.password); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const lo = tapPolicy?.minimumLifetimeInMinutes || 10;
  const hi = tapPolicy?.maximumLifetimeInMinutes || 43200;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="reissue-tap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm"><RefreshCw size={16} /> {t("pw.reissueTitle")}</DialogTitle>
          <DialogDescription className="text-xs">{t("pw.reissueDesc")} <strong>{employeeName}</strong></DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="reissue-tap-result">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("pw.tapValue")}</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded-sm border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-sm" data-testid="reissue-tap-value">{result.password}</code>
                <Button variant="outline" size="sm" onClick={copy} data-testid="reissue-tap-copy" className="rounded-sm border-zinc-300">{copied ? <Check size={15} /> : <Copy size={15} />}</Button>
              </div>
            </div>
            <div className="space-y-1 text-xs text-zinc-500">
              <div>{t("pw.tapExpires")}: <strong>{result.lifetimeInMinutes} min</strong></div>
              {result.notify && <div>{t("pw.notify")}: <strong>{result.notify}</strong></div>}
            </div>
            <p className="text-[11px] text-amber-600">{t("pw.copyWarn")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800" data-testid="reissue-tap-info">
              <Timer size={14} className="mt-0.5 shrink-0" /> <span>{t("pw.reissueInfo")}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("pw.tapLifetime")}</Label>
                <Input type="number" min={lo} max={hi} value={tapLifetime} onChange={(e) => setTapLifetime(e.target.value)} data-testid="reissue-tap-lifetime" className="rounded-sm border-zinc-300 font-mono text-sm" />
                {tapPolicy && <p className="text-[11px] text-zinc-400">{t("pw.tapBounds").replace("{min}", lo).replace("{max}", hi)}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("pw.tapStart")}</Label>
                <Input type="datetime-local" value={tapStart} onChange={(e) => setTapStart(e.target.value)} data-testid="reissue-tap-start" className="rounded-sm border-zinc-300 text-sm" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("pw.notifyContact")}</Label>
              {picked ? (
                <div className="flex items-center justify-between rounded-sm border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                  <span className="text-zinc-700">{picked.displayName} <span className="text-zinc-400">· {picked.mail || picked.userPrincipalName}</span></span>
                  <button onClick={() => setContactId("")} data-testid="reissue-clear-contact"><X size={14} /></button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pw.searchContact")} className="rounded-sm border-zinc-300 pl-8 text-sm" data-testid="reissue-contact-search" />
                  </div>
                  {q && (
                    <div className="max-h-32 overflow-auto rounded-sm border border-zinc-200">
                      {list.map((u) => (
                        <button key={u.id} onClick={() => { setContactId(u.id); setQ(""); }} data-testid={`reissue-contact-${u.id}`} className="flex w-full items-center justify-between border-b border-zinc-100 px-2 py-1.5 text-left text-sm last:border-0 hover:bg-zinc-50">
                          <span className="truncate text-zinc-700">{u.displayName}</span>
                          <span className="ml-auto truncate text-[11px] text-zinc-400">{u.mail || u.userPrincipalName}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-400">{t("pw.tapNotifyHint")}</p>
                </>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)} className="rounded-sm bg-primary hover:bg-[#002480]" data-testid="reissue-close">{t("common.close") || "Close"}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="reissue-cancel">{t("common.cancel")}</Button>
              <Button onClick={submit} disabled={busy} data-testid="reissue-submit" className="rounded-sm bg-primary hover:bg-[#002480]">
                {busy && <Loader2 size={15} className="mr-2 animate-spin" />}{t("pw.reissueCreate")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
